import { Box, ButtonV2, useToast } from '@withjoy/joykit';
import type { FC } from 'react';
import React, { useCallback, useRef } from 'react';
import { StyledImage, styles } from './EventPhoto.styles';
import { TempSpinner } from '../../../../../../../shared/components/TempSpinner';
import { PhotoMenu } from '../../../../../../../shared/components/PhotoMenu';
import { ReactComponent as CameraIcon } from '@assets/icons/camera-outline.svg';
import { useRegistryGuestTranslations } from '@apps/registry/guest/routes/GuestRegistry/GuestRegistry.i18n';
import { uploadedImageAssetId, useImageUpload } from '@shared/components/UploadWidget/useImageUpload';
import { useRemoveEventPagePhotoMutation } from '@graphql/mutations/registry.graphql.generated';
import { useUploadPhotoFromUrlMutation } from '@shared/components/PhotoUpload/useContainerPhotoUpload.graphql.generated';
import { useSaveNativeEventPagePhoto } from '@shared/components/PhotoUpload/useSaveNativeEventPagePhoto';
import { useUpdateRegistryPageContainerPhotoMutation } from './EventPhoto.graphql.generated';
import type { ApolloCache } from '@apollo/client';
import { IconCard } from '../../IconCard';
import { Photos } from '@withjoy/joykit/icons';

type Props = {
  eventPageId?: string;
  // PRNT-4193 — when set, the registry page is migrated and writes route through
  // `updatePageContainer` (after `uploadPhotoFromUrl`) instead of legacy
  // `setEventPageAsset` / `removeEventPagePhoto`.
  migratedPageContainerId?: string;
  handleImageLoad?(): void;
  onUpdatePhoto?(previewUrl?: string): void;
  onDeletePhoto?(): void;
  photo?: string;
  showEdit?: boolean;
  firebaseId?: string;
  showEmptyCard?: boolean;
};

export const EventPhoto: FC<Props> = ({
  eventPageId,
  migratedPageContainerId,
  handleImageLoad,
  photo,
  showEdit = false,
  onDeletePhoto,
  onUpdatePhoto,
  firebaseId = '',
  showEmptyCard = false,
}) => {
  const isMigrated = !!migratedPageContainerId;
  const hasTarget = isMigrated || !!eventPageId;
  const canEdit = showEdit && hasTarget;
  const photoRef = useRef<HTMLImageElement>(null);
  const { getPhotoMenuTranslations } = useRegistryGuestTranslations();

  const { toast } = useToast();
  const { error, success } = getPhotoMenuTranslations();

  const updateLegacyPhotoInCache = useCallback(
    ({ cache, url }: { cache: ApolloCache<unknown>; url: string | null }) => {
      cache.modify({
        id: cache.identify({ __typename: 'EventPage', id: eventPageId }),
        fields: {
          photoV2(existingPhotoRef) {
            return url
              ? {
                  ...existingPhotoRef,
                  url: url,
                }
              : null;
          },
        },
      });
    },
    [eventPageId],
  );

  const { saveNativeEventPagePhoto, isSavingNativePagePhoto: isUpdatingLegacyPhoto } = useSaveNativeEventPagePhoto();

  const [removeEventPagePhoto, { loading: isDeletingLegacyPhoto }] = useRemoveEventPagePhotoMutation({
    onError: () => {
      toast(error);
    },
    onCompleted: () => {
      toast(success);
      onDeletePhoto?.();
    },
  });

  // No onError on these two: with one, Apollo resolves a failed mutation instead of rejecting, and the upload
  // widget would then report the photo as saved. Each caller shows the failure itself.
  const [uploadPhotoFromUrl, { loading: isUploadingMigratedPhoto }] = useUploadPhotoFromUrlMutation();
  const [updatePageContainerPhoto, { loading: isUpdatingMigratedPhoto }] = useUpdateRegistryPageContainerPhotoMutation();

  const handleDeletePhoto = useCallback(async () => {
    if (isMigrated && migratedPageContainerId) {
      try {
        const { errors } = await updatePageContainerPhoto({
          variables: { payload: { id: migratedPageContainerId, mainPhotoId: null } },
        });
        if (errors) {
          toast(error);
          return;
        }
        toast(success);
        onDeletePhoto?.();
      } catch {
        toast(error);
      }
      return;
    }
    if (!eventPageId) return;
    removeEventPagePhoto({
      variables: { eventPageId },
      update: cache => {
        updateLegacyPhotoInCache({ cache, url: null });
      },
    });
  }, [isMigrated, migratedPageContainerId, updatePageContainerPhoto, eventPageId, removeEventPagePhoto, updateLegacyPhotoInCache, toast, success, error, onDeletePhoto]);

  // Both containers below need the bytes copied in from the upload's URL, so a widget upload and a Filestack one
  // take the same path.
  const {
    open: openFilePicker,
    uploadWidget,
    isUploading,
  } = useImageUpload({
    containerId: firebaseId || '',
    maxFiles: 1,
    useCase: { area: 'admin', subArea: 'registry' },
    onError: () => toast(error),
    onUpload: async images => {
      const image = images[0];
      if (!image) return;
      // Every failure below throws: onError shows it, and the widget stays open with the file staged.
      const { width, height } = image;
      if (!width || !height) throw new Error('Event photo preview failed to load');
      const previewUrl = image.url;
      const assetId = uploadedImageAssetId(image);
      // The widget shows its own "1 photo uploaded" toast.
      const showSuccessToast = image.source === 'filestack';

      if (isMigrated && migratedPageContainerId) {
        const { data } = await uploadPhotoFromUrl({
          variables: { payload: { assetId, containerId: migratedPageContainerId, url: previewUrl } },
        });
        const mainPhotoId = data?.uploadPhotoFromUrl?.id;
        if (!mainPhotoId) throw new Error('uploadPhotoFromUrl returned no photo');
        const { errors } = await updatePageContainerPhoto({
          variables: { payload: { id: migratedPageContainerId, mainPhotoId } },
        });
        if (errors) throw new Error('updatePageContainer returned errors');
        onUpdatePhoto?.(previewUrl);
        if (showSuccessToast) toast(success);
        return;
      }

      if (!eventPageId) return;
      const { data } = await saveNativeEventPagePhoto({
        containerId: firebaseId,
        eventPageId,
        previewUrl,
        assetPayload: {
          assetId,
          mimeType: image.mimeType ?? 'image/jpeg',
          fileSize: image.fileSize ?? 0,
          width,
          height,
        },
        setAssetOptions: {
          update: cache => {
            updateLegacyPhotoInCache({ cache, url: previewUrl });
          },
        },
      });
      onUpdatePhoto?.(data?.setEventPageAsset?.photoV2?.url);
      if (showSuccessToast) toast(success);
    },
  });

  const isUpdating = isDeletingLegacyPhoto || isUpdatingLegacyPhoto || isUploadingMigratedPhoto || isUpdatingMigratedPhoto || isUploading;

  const handleOnUpdatePhoto = useCallback(() => {
    openFilePicker();
  }, [openFilePicker]);

  const canShowEmptyCard = showEmptyCard && hasTarget;
  const placeholder = canShowEmptyCard ? (
    <IconCard
      onClick={handleOnUpdatePhoto}
      text="Set your photo"
      icon={<Photos />}
    />
  ) : (
    <Box __css={styles.emptyImage} />
  );
  const isEmptyCardVisible = !photo && canShowEmptyCard;

  return (
    <Box
      maxWidth={{ md: isEmptyCardVisible ? '100%' : '36rem' }}
      width={{ _: '100%', md: isEmptyCardVisible ? '100%' : '25.6rem' }}
      position="relative"
      opacity={isUpdating ? '0.3' : '1'}
    >
      {photo ? (
        <StyledImage
          ref={photoRef}
          src={photo}
          onLoad={handleImageLoad}
        />
      ) : (
        placeholder
      )}
      {isUpdating && <TempSpinner position={'absolute'} />}
      {uploadWidget}
      {!isEmptyCardVisible && canEdit && (
        <Box
          position="absolute"
          left={0}
          top={0}
          margin={6}
        >
          <PhotoMenu
            handleUpdatePhoto={handleOnUpdatePhoto}
            handleDeletePhoto={handleDeletePhoto}
            photoExists
          >
            <ButtonV2
              background="rgba(0, 0, 0, 0.80)"
              padding={4}
              borderRadius="100%"
              intent="neutral"
            >
              <CameraIcon />
            </ButtonV2>
          </PhotoMenu>
        </Box>
      )}
    </Box>
  );
};
